import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler, inject } from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';

import { AppComponent } from './app.component';
import { RouterModule, Route } from '@angular/router';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  RavenErrorLoggingService,
  createRavenErrorHandler,
} from '@nuso/common/util-errors';

import { createReducers, LoginStoreAdapterService } from '@nuso/portal/state';
import * as Sentry from '@sentry/angular';

import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache, ApolloLink } from '@apollo/client';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import {
  createInitialNetworkStatusState,
  NetworkStatusUpdateService,
} from '@nuso/common/data-access-api';
import { LoginStateManagementService, createInitialLoginState } from '@nuso/auth/data-access';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NetworkLostInterceptor, XTabIdInterceptor } from '@nuso/common/data-access-api';

import {
  AppEnvironment,
  PortalAppFeatures,
  isProductionToken,
  appEnvironmentToken,
  portalAppFeaturesToken,
} from '@nuso/portal/environments';
import {
  HttpClient,
  HttpClientModule,
  HttpErrorResponse,
} from '@angular/common/http';
import { createApollo } from '@nuso/common/data-access-api';
import { createRavenErrorLoggingService } from '@nuso/common/util-errors';

import { CpaasUserService, CpaasCustomerService } from '@nuso/cpaas/data-access';
import { PortalCpaasUserService, PortalCpaasCustomerService } from '@nuso/cpaas/portal-data-access';
import { ServicesService } from '@nuso/portal/data-access-services';
import { gql } from 'graphql-tag';

if (environment.sentry) {
  Sentry.init({
    dsn: environment.sentry,
    environment: environment.envName,
    release: window['MARKETSPACE_VERSION'],
  });
}

const appEnvironment: AppEnvironment = {
  envName: environment.envName,
  appName: environment.appName,
  production: environment.production,
  sentry: environment.sentry,
  showDebug: environment.showDebug,
  statusPageIoEmbedUrl: environment.statusPageIoEmbedUrl,
};

const portalAppFeatures: PortalAppFeatures = environment;

const mainRoute: Route = {
  path: '',
  loadChildren: () =>
    import('@nuso/portal/feature-shell').then(
      (m) => m.PortalFeatureShellModule
    ),
  // You could put authentication guards here to run checks before lazy-loading main
  canLoad: [],
  canActivate: [],
};

const calcRoute: Route = {
  path: 'calc',
  loadChildren: () =>
    import('@nuso/portal/feature-bandwidth-calculator').then(
      (m) => m.PortalFeatureBandwidthCalculatorModule
    ),
  // You could put authentication guards here to run checks before lazy-loading main
  canLoad: [],
  canActivate: [],
};

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

function createInitialAppState() {
  return {
    ROOT_QUERY: {
      ...createInitialNetworkStatusState(),
      ...createInitialLoginState(),
    },
  };
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        RouterModule.forRoot([calcRoute, mainRoute], {
            initialNavigation: 'enabledBlocking'
        }),
        MatSnackBarModule,
        // Below was added when we tried creating an empty store app
        // commenting out for now, and leaving the forRoot in the
        // feature-monolith... once we can break state into own lib, can move
        // back here (or not.)
        // might be best to move things into forFeature pieces
        StoreModule.forRoot(createReducers(), {
            initialState: {
                loginStatus: {
                    id: undefined,
                    username: undefined,
                    pending: true, // NEED THIS FOR GUARD
                    isLoggedIn: false,
                    roles: [],
                    permissions: [],
                    redirectUrl: null, // this will need to get set in the reducer to window.location for AOT
                    impersonatorId: null,
                    resellerCustomer: null,
                },
            },
            metaReducers: !environment.production ? [] : [],
            runtimeChecks: {
                strictActionImmutability: true,
                strictStateImmutability: true,
            },
        }),
        // EffectsModule.forRoot([]),
        !environment.production
            ? StoreDevtoolsModule.instrument({ maxAge: 50, connectInZone: true })
            : []], providers: [
        // apollo-angular 10 removed ApolloModule, which only provided Apollo.
        Apollo,
        { provide: isProductionToken, useValue: environment.production },
        { provide: appEnvironmentToken, useValue: appEnvironment },
        { provide: portalAppFeaturesToken, useValue: portalAppFeatures },
        {
            provide: InMemoryCache,
            useValue: new InMemoryCache({
                typePolicies: {
                    IPFaxCoverPageFields: {
                        keyFields: ['id', 'type'],
                    },
                    OSSCallFwdRule: {
                        keyFields: ['targetId', 'cfType', 'dialedDid'],
                    },
                    TnDetail: {
                        keyFields: ['tn'],
                    },
                    CampaignDid: {
                        keyFields: ['did']
                    },
                    CpaasTn: {
                        keyFields: ['did'],
                    },
                    Customer: {
                        fields: {
                            pbxInfo: {
                                merge: true
                            }
                        }
                    },
                    RecordingCallTag: {
                        keyFields: ['tagId'],
                    },
                    CustomerRecordingSource: {
                        keyFields: ['serviceId'],
                    },
                    ExportFieldFlags: { keyFields: false },
                    RecordingCall: {
                        fields: {
                            tags: {
                                merge(existing = [], incoming: any[]) {
                                    return [...incoming]; // Replace the old tags with the new ones
                                }
                            },
                            comments: {
                                merge(existing = [], incoming: any[]) {
                                    return [...incoming];
                                }
                            }
                        }
                    },
                    ServiceType: {
                        keyFields: ['type'],
                    },
                    PbxVerificationInfo: {
                        fields: {
                            pbxDetails: {
                                merge: true
                            },
                            ossSipDetails: {
                                merge: true
                            }
                        }
                    },
                    // Probably should move the below into another library, but the intent
                    // is to automatically update a cached Line when we update LineDetails
                    LineDetails: {
                        merge(existing, incoming, { cache }) {
                            if (typeof incoming === 'object' && incoming.__ref) {
                                const fullCache = cache.extract();
                                const details = fullCache[incoming.__ref];
                                if (details) {
                                    const line = fullCache[`Line:${details.id}`];
                                    if (line) {
                                        // check to see if fields we care about have changed
                                        cache.modify({
                                            id: `Line:${details.id}`,
                                            fields: {
                                                name: () => details.name,
                                                extension: () => details.extension,
                                                type: () => details.type,
                                                number: () => details.number,
                                                classOfService: () => details.classOfService,
                                                persistentProfile: () => details.persistentProfile,
                                                licenseType: () => details.licenseType,
                                                numberDeletedFromCustomer: () => details.numberDeletedFromCustomer,
                                                numberOrderStatus: () => details.numberOrderStatus,
                                                addOns: () => details.addOns,
                                            }
                                        });
                                    }
                                }
                            }
                            return { ...existing, ...incoming };
                        }
                    }
                },
            }).restore(createInitialAppState()),
        },
        {
            provide: ErrorHandler,
            // Log to the console in every environment, as Angular's default handler does: visual-net
            // fails a capture on console errors, and Sentry alone is invisible from the browser.
            useValue: createRavenErrorHandler(environment.production, true),
        },
        {
            provide: RavenErrorLoggingService,
            useValue: createRavenErrorLoggingService(environment.production),
        },
        {
            provide: APOLLO_OPTIONS,
            useFactory: createApollo,
            deps: [
                HttpLink,
                InMemoryCache,
                NetworkStatusUpdateService,
                isProductionToken,
            ],
        },
        { provide: HTTP_INTERCEPTORS, useClass: XTabIdInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: NetworkLostInterceptor, multi: true },
        ServicesService,
        { provide: CpaasUserService, useClass: PortalCpaasUserService },
        { provide: CpaasCustomerService, useClass: PortalCpaasCustomerService },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
  private apollo = inject(Apollo);
  private cache = inject(InMemoryCache);
  private translate = inject(TranslateService);
  private loginStateService = inject(LoginStateManagementService);
  private loginStoreAdapter = inject(LoginStoreAdapterService);

  constructor() {
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    //  TODO: we may change this based on the user, or perhaps the brand...
    this.translate.use('en');


    console.log(environment.envName, window['MARKETSPACE_VERSION']);
    this.apollo.client.onClearStore(() => {
      console.log('RESETTING STORE');
      this.cache.restore(createInitialAppState());
      return Promise.resolve();
    });

    this.loginStoreAdapter.init();
    this.loginStateService.init();
  }
}
